<template>
  <div class="practice-assistant">
    <PracticeAssistantList
      ref="practiceAssistantListRef"
      @create="handleCreate"
      @redispatch="handleRedispatch"
    />

    <DialogLayout
      v-model:show="taskDialogVisible"
      :before-close="handleTaskBeforeClose"
      :max-height="800"
      :max-width="1008"
      :min-height="600"
      :min-width="800"
      :title="dialogTitle"
    >
      <PracticeAssistantForm
        v-model:form="form"
        :active-step="activeStep"
        :dialog-type="dialogType"
        :extract-status="extractStatus"
        :profile-status="profileStatus"
        :save-loading="saveLoading"
        :step-one-expanded="stepOneExpanded"
        @extract="handleExtract"
        @generate-profiles="handleGenerateProfiles"
        @prev-step="handlePrevStep"
        @save="handleSave"
        @toggle-step-one="handleToggleStepOne"
      />
    </DialogLayout>

    <PracticeAssistantFormReDialog
      v-model:show="redispatchDialogVisible"
      :before-close="handleRedispatchBeforeClose"
      :params="redispatchParams"
      @cancel="handleCancelRedispatch"
      @loaded="handleRedispatchLoaded"
      @success="handleRedispatchSuccess"
      @update:form="handleRedispatchFormChange"
    />
  </div>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import DialogLayout from '@/components/layout/dialog-layout/index.vue'
import PracticeAssistantList from './components/practice-assistant-list.vue'
import PracticeAssistantForm from './components/practice-assistant-form.vue'
import PracticeAssistantFormReDialog from './components/practice-assistant-form-re/practice-assistant-form-re-dialog.vue'
import utils from '@/utils'

import {
  createThemePracticeTask,
  extractThemePracticeTask,
  getThemePracticeTaskExtractResult,
  previewThemePracticeTaskProfiles,
  getThemePracticeTaskPreviewProfilesResult
} from '@/api/index.js'

defineOptions({
  name: 'PracticeAssistant'
})

const practiceAssistantListRef = ref(null)
const saveLoading = ref(false)
const taskDialogVisible = ref(false)
const redispatchDialogVisible = ref(false)
const dialogType = ref('create')
const currentTaskId = ref('')
const redispatchParams = ref({})
const savedFormSnapshot = ref('')
const profilePayloadSnapshot = ref('')
const extractStatus = ref('idle')
const profileStatus = ref('idle')
const activeStep = ref('step-one')
const stepOneExpanded = ref(true)
const form = ref(createDefaultForm())
const EXTRACT_POLL_INTERVAL = 5000
const EXTRACT_POLL_MAX_COUNT = 60
const PROFILE_POLL_INTERVAL = 5000
const PROFILE_POLL_MAX_COUNT = 60

let extractPollingTimer = null
let extractPollingResolve = null
let extractPollingToken = 0
let profilePollingTimer = null
let profilePollingResolve = null
let profilePollingToken = 0

const dialogTitle = computed(() => {
  return dialogType.value === 'create' ? '新建任务' : '编辑任务'
})

const closeConfirmTitle = computed(() => {
  const titleMap = {
    create: '关闭新建',
    redispatch: '关闭再次派发',
    edit: '关闭编辑'
  }
  return titleMap[dialogType.value] || titleMap.edit
})

function getCurrentUserRange() {
  const userId = utils.xbbLocalStorageGet('userId')
  const userName = utils.xbbLocalStorageGet('userName')

  return userId
    ? [
        {
          id: userId,
          name: userName,
          property: 'user'
        }
      ]
    : []
}

function getDefaultDueTime() {
  const defaultDueTime = new Date()
  defaultDueTime.setDate(defaultDueTime.getDate() + 7)
  defaultDueTime.setSeconds(0, 0)

  return Math.floor(defaultDueTime.getTime() / 1000)
}

function createDefaultForm() {
  return {
    trainingContent: '',
    supplementFile: [],
    themeName: '',
    userRangePojoList: getCurrentUserRange(),
    themeDesc: '',
    dueTime: getDefaultDueTime(),
    supplementSummaryFileUrl: '',
    customerProfileList: [],
    selectedProfileIds: [],
    taskNodeResult: {},
    profileNodeResult: {}
  }
}

const resetForm = () => {
  form.value = createDefaultForm()
  currentTaskId.value = ''
  redispatchParams.value = {}
  profilePayloadSnapshot.value = ''
  extractStatus.value = 'idle'
  profileStatus.value = 'idle'
  activeStep.value = 'step-one'
  stepOneExpanded.value = true
}

const getComparableForm = (formValue = {}) => ({
  trainingContent: formValue.trainingContent || '',
  supplementFile: normalizeSupplementFile(formValue.supplementFile).map((file) => ({
    url: file.url,
    fileName: file.fileName,
    suffix: file.suffix,
    size: file.size || 0
  })),
  themeName: formValue.themeName || '',
  userRangePojoList: normalizeUserRange(formValue.userRangePojoList),
  themeDesc: formValue.themeDesc || '',
  dueTime: formValue.dueTime || '',
  supplementSummaryFileUrl: formValue.supplementSummaryFileUrl || '',
  customerProfileList: normalizeCustomerProfileList(formValue.customerProfileList),
  selectedProfileIds: normalizeSelectedProfileIds(formValue.selectedProfileIds),
  taskNodeResult: formValue.taskNodeResult || {},
  profileNodeResult: formValue.profileNodeResult || {}
})

const updateSavedFormSnapshot = () => {
  savedFormSnapshot.value = JSON.stringify(getComparableForm(form.value))
}

const hasUnsavedChange = computed(() => {
  if (!taskDialogVisible.value && !redispatchDialogVisible.value) {
    return false
  }

  return JSON.stringify(getComparableForm(form.value)) !== savedFormSnapshot.value
})

const getRangeFallbackName = (item = {}) => {
  const rangeTypeText = item.property === 'dept' ? '部门' : '成员'
  return item.id ? `${rangeTypeText}(${item.id})` : rangeTypeText
}

const normalizeUserRange = (userRange) => {
  if (!Array.isArray(userRange)) {
    return []
  }

  return userRange
    .map((item) => {
      const id = item?.id || item?.userId || item?.deptId || ''
      const property = item?.property || 'user'

      return {
        id,
        name:
          item?.name ||
          item?.userName ||
          item?.deptName ||
          item?.label ||
          getRangeFallbackName({ id, property }),
        property
      }
    })
    .filter((item) => item.id)
}

const getFileNameFromUrl = (url = '') => {
  const filePath = String(url).split('?')[0]
  const fileName = filePath.substring(filePath.lastIndexOf('/') + 1)

  return decodeURIComponent(fileName || '已上传文件')
}

const getFileExtFromName = (fileName = '') => {
  const lastDotIndex = fileName.lastIndexOf('.')
  return lastDotIndex > -1 ? fileName.slice(lastDotIndex + 1).toLowerCase() : ''
}

const normalizeFileSize = (size) => {
  const parsedSize = Number(size)
  return Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : undefined
}

const normalizeSupplementFile = (fileList) => {
  if (!Array.isArray(fileList)) {
    return []
  }

  return fileList
    .map((item, index) => {
      const url = String(item?.url || item?.attachIndex || '').trim()
      const fileName =
        String(item?.fileName || item?.filename || '').trim() || getFileNameFromUrl(url)

      if (!url) {
        return null
      }

      const suffix = String(item?.suffix || item?.ext || getFileExtFromName(fileName)).toLowerCase()
      const size = normalizeFileSize(item?.size)

      return {
        id: item?.id || `supplement-file-${index}-${url}`,
        url,
        attachIndex: url,
        fileName,
        filename: fileName,
        suffix,
        ext: suffix,
        ...(size ? { size } : {})
      }
    })
    .filter(Boolean)
}

const getProfileId = (profile, index = 0) => String(profile?.profileNo || index + 1)

const normalizeCustomerProfileList = (profileList) => {
  if (!Array.isArray(profileList)) {
    return []
  }

  return profileList
    .map((item, index) => {
      if (!item) {
        return null
      }

      const customerProfile = {
        profileNo: Number(item.profileNo || index + 1),
        customerName: String(item.customerName || `客户画像${index + 1}`),
        background: String(item.background || ''),
        initialAttitude: String(item.initialAttitude || ''),
        trainingGoal: String(item.trainingGoal || ''),
        profile: String(item.profile || ''),
        customerQuote: String(item.customerQuote || ''),
        id: getProfileId(item, index)
      }

      if (item.meetingCount !== undefined) {
        customerProfile.meetingCount = item.meetingCount
      }

      return customerProfile
    })
    .filter(Boolean)
}

const normalizeSelectedProfileIds = (selectedProfileIds) => {
  return Array.isArray(selectedProfileIds)
    ? selectedProfileIds.map((item) => String(item)).filter(Boolean)
    : []
}

const getSelectedProfileList = () => {
  const selectedIdSet = new Set(normalizeSelectedProfileIds(form.value.selectedProfileIds))
  return normalizeCustomerProfileList(form.value.customerProfileList).filter((item) =>
    selectedIdSet.has(item.id)
  )
}

const buildCustomerProfilePayload = () => {
  return getSelectedProfileList().map(({ id, ...profile }) => profile)
}

const buildSupplementFilePayload = () => {
  return normalizeSupplementFile(form.value.supplementFile).map(
    ({ url, suffix, fileName, size }) => ({
      url,
      suffix,
      fileName,
      ...(size ? { size } : {})
    })
  )
}

const buildSubmitPayload = () => {
  const userRangePojoList = normalizeUserRange(form.value.userRangePojoList)

  if (dialogType.value === 'redispatch') {
    return {
      sourceTaskId: currentTaskId.value,
      userRangePojoList,
      dueTime: form.value.dueTime,
      customerProfileList: buildCustomerProfilePayload()
    }
  }

  return {
    trainingContent: form.value.trainingContent.trim(),
    supplementFile: buildSupplementFilePayload(),
    themeName: form.value.themeName.trim(),
    themeDesc: form.value.themeDesc.trim(),
    supplementSummaryFileUrl: form.value.supplementSummaryFileUrl || '',
    userRangePojoList,
    customerProfileList: buildCustomerProfilePayload(),
    dueTime: form.value.dueTime,
    taskNodeResult: form.value.taskNodeResult || {},
    profileNodeResult: form.value.profileNodeResult || {}
  }
}

const buildPreviewProfilesPayload = () => {
  return {
    themeName: form.value.themeName.trim(),
    themeDesc: form.value.themeDesc.trim(),
    userRangePojoList: normalizeUserRange(form.value.userRangePojoList)
  }
}

const getFirstMeaningfulLine = (text = '') => {
  return String(text)
    .split(/\r?\n/)
    .map((item) => item.trim())
    .find(Boolean)
}

const buildTaskNameFromContent = (content = '') => {
  const firstLine = getFirstMeaningfulLine(content) || ''
  const normalizedLine = firstLine
    .replace(/^[0-9一二三四五六七八九十]+[、.．\s-]*/, '')
    .replace(/[：:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return normalizedLine.slice(0, 20)
}

const clearExtractPolling = () => {
  extractPollingToken += 1

  if (extractPollingTimer) {
    clearTimeout(extractPollingTimer)
    extractPollingTimer = null
  }

  if (extractPollingResolve) {
    extractPollingResolve()
    extractPollingResolve = null
  }
}

const clearProfilePolling = () => {
  profilePollingToken += 1

  if (profilePollingTimer) {
    clearTimeout(profilePollingTimer)
    profilePollingTimer = null
  }

  if (profilePollingResolve) {
    profilePollingResolve()
    profilePollingResolve = null
  }
}

const waitExtractPollingInterval = () => {
  return new Promise((resolve) => {
    extractPollingResolve = resolve
    extractPollingTimer = setTimeout(() => {
      extractPollingTimer = null
      extractPollingResolve = null
      resolve()
    }, EXTRACT_POLL_INTERVAL)
  })
}

const waitProfilePollingInterval = () => {
  return new Promise((resolve) => {
    profilePollingResolve = resolve
    profilePollingTimer = setTimeout(() => {
      profilePollingTimer = null
      profilePollingResolve = null
      resolve()
    }, PROFILE_POLL_INTERVAL)
  })
}

const isExtractSuccess = (result = {}) => Number(result.status) === 1
const isExtractFailed = (result = {}) => Number(result.status) === 2
const isExtractProcessing = (result = {}) => Number(result.status) === 0

const pollExtractResult = async (initialResult = {}) => {
  if (isExtractFailed(initialResult)) {
    throw new Error(initialResult.failReason || 'AI 提炼失败，请稍后重试')
  }

  if (!isExtractProcessing(initialResult)) {
    return initialResult
  }

  const pollId = initialResult.id
  if (!pollId) {
    throw new Error('未获取到提炼任务 ID，请稍后重试')
  }

  const currentToken = extractPollingToken

  for (let count = 0; count < EXTRACT_POLL_MAX_COUNT; count += 1) {
    await waitExtractPollingInterval()

    if (currentToken !== extractPollingToken) {
      const cancelError = new Error('提炼任务已取消')
      cancelError.cancelled = true
      throw cancelError
    }

    const res = await getThemePracticeTaskExtractResult({
      id: pollId
    })
    const extractResult = res.result || {}

    if (isExtractSuccess(extractResult)) {
      return extractResult
    }

    if (isExtractFailed(extractResult)) {
      throw new Error(extractResult.failReason || 'AI 提炼失败，请稍后重试')
    }
  }

  throw new Error('AI 提炼超时，请稍后重试')
}

const isProfileSuccess = (result = {}) => Number(result.status) === 1
const isProfileFailed = (result = {}) => Number(result.status) === 2
const isProfileProcessing = (result = {}) => Number(result.status) === 0

const createProfileCancelledError = () => {
  const cancelError = new Error('画像生成任务已取消')
  cancelError.cancelled = true
  return cancelError
}

const ensureProfilePollingActive = (currentToken) => {
  if (currentToken !== profilePollingToken) {
    throw createProfileCancelledError()
  }
}

const pollProfileResult = async (initialResult = {}, currentToken) => {
  ensureProfilePollingActive(currentToken)

  if (isProfileFailed(initialResult)) {
    throw new Error(initialResult.failReason || '客户画像生成失败，请稍后重试')
  }

  if (!isProfileProcessing(initialResult)) {
    return initialResult
  }

  const pollId = initialResult.id
  if (!pollId) {
    throw new Error('未获取到画像生成任务 ID，请稍后重试')
  }

  for (let count = 0; count < PROFILE_POLL_MAX_COUNT; count += 1) {
    await waitProfilePollingInterval()
    ensureProfilePollingActive(currentToken)

    const res = await getThemePracticeTaskPreviewProfilesResult({
      id: pollId
    })
    const profileResult = res.result || {}

    if (isProfileSuccess(profileResult)) {
      return profileResult
    }

    if (isProfileFailed(profileResult)) {
      throw new Error(profileResult.failReason || '客户画像生成失败，请稍后重试')
    }
  }

  throw new Error('客户画像生成超时，请稍后重试')
}

const generateCustomerProfileResult = async (currentToken) => {
  const res = await previewThemePracticeTaskProfiles(buildPreviewProfilesPayload())
  ensureProfilePollingActive(currentToken)
  return pollProfileResult(res.result || {}, currentToken)
}

const applyExtractResult = (extractResult = {}, trainingContent = '') => {
  form.value = {
    ...form.value,
    themeName:
      String(extractResult.themeName || '').trim() ||
      buildTaskNameFromContent(trainingContent) ||
      form.value.themeName,
    themeDesc: String(extractResult.themeDesc || '').trim(),
    supplementSummaryFileUrl: extractResult.supplementSummaryFileUrl || '',
    taskNodeResult: extractResult.taskNodeResult || {},
    profileNodeResult: {}
  }
}

const refreshList = async () => {
  await practiceAssistantListRef.value?.fetchList()
}

const handleCreate = () => {
  resetForm()
  updateSavedFormSnapshot()
  dialogType.value = 'create'
  taskDialogVisible.value = true
}

const handleRedispatch = (item) => {
  if (!item?.id) {
    return
  }

  resetForm()
  dialogType.value = 'redispatch'
  currentTaskId.value = item.id
  redispatchParams.value = {
    id: item.id
  }
  updateSavedFormSnapshot()
  redispatchDialogVisible.value = true
}

const handleRedispatchLoaded = ({ detail, form: redispatchForm }) => {
  currentTaskId.value = detail?.id || redispatchParams.value.id || currentTaskId.value
  form.value = redispatchForm || form.value
  updateSavedFormSnapshot()
}

const handleRedispatchFormChange = (redispatchForm) => {
  form.value = redispatchForm || form.value
}

const handleRedispatchSuccess = async () => {
  redispatchDialogVisible.value = false
  await refreshList()
}

const handleBeforeClose = (done) => {
  if (!hasUnsavedChange.value) {
    done()
    return
  }

  ElMessageBox.confirm(
    '检测到已有编辑内容，关闭后，本次未提交内容将不会保留，确定关闭吗？',
    closeConfirmTitle.value,
    {
      cancelButtonText: '取消',
      confirmButtonText: '确定关闭',
      customClass: 'practice-assistant__close-confirm',
      distinguishCancelAndClose: true,
      type: ''
    }
  )
    .then(() => {
      done()
    })
    .catch(() => {})
}

const handleTaskBeforeClose = (done) => {
  handleBeforeClose(done)
}

const handleRedispatchBeforeClose = (done) => {
  handleBeforeClose(done)
}

const handleCancelRedispatch = () => {
  handleRedispatchBeforeClose(() => {
    redispatchDialogVisible.value = false
  })
}

const handleToggleStepOne = () => {
  if (extractStatus.value !== 'done') {
    return
  }

  stepOneExpanded.value = !stepOneExpanded.value
}

const clearProfiles = () => {
  profilePayloadSnapshot.value = ''
  form.value = {
    ...form.value,
    customerProfileList: [],
    selectedProfileIds: [],
    profileNodeResult: {}
  }
}

const handlePrevStep = (stepName) => {
  if (stepName === 'step-three') {
    if (profileStatus.value === 'loading') {
      return
    }

    profilePayloadSnapshot.value = getProfilePayloadSnapshot()
    activeStep.value = 'step-two'
    return
  }

  if (extractStatus.value !== 'done') {
    return
  }

  clearProfilePolling()
  profileStatus.value = 'idle'
  clearProfiles()
  activeStep.value = 'step-one'
  stepOneExpanded.value = true
}

const handleExtract = async () => {
  const trainingContent = form.value.trainingContent.trim()

  if (!trainingContent) {
    ElMessage.warning('请输入想要训练的内容')
    return
  }

  if (extractStatus.value === 'loading') {
    return
  }

  const previousStatus = extractStatus.value
  clearExtractPolling()
  clearProfilePolling()
  activeStep.value = 'step-two'
  stepOneExpanded.value = false
  profileStatus.value = 'idle'
  clearProfiles()
  extractStatus.value = 'loading'

  try {
    const res = await extractThemePracticeTask({
      trainingContent,
      supplementFile: buildSupplementFilePayload()
    })
    const extractResult = await pollExtractResult(res.result || {})

    applyExtractResult(extractResult, trainingContent)

    extractStatus.value = 'done'
    activeStep.value = 'step-two'
    stepOneExpanded.value = false
  } catch (error) {
    // if (!error?.cancelled) {
    //   ElMessage.error(error?.message || 'AI 提炼失败，请稍后重试')
    // }
    extractStatus.value = previousStatus === 'done' ? 'done' : 'idle'
  }
}

const validateStepTwoForm = () => {
  const trainingContent = form.value.trainingContent.trim()
  const themeName = form.value.themeName.trim()
  const themeDesc = form.value.themeDesc.trim()
  const dueTime = form.value.dueTime
  const userRangePojoList = normalizeUserRange(form.value.userRangePojoList)

  if (!trainingContent) {
    ElMessage.warning('请输入想要训练的内容')
    return false
  }

  if (!themeName) {
    ElMessage.warning('请输入主题名称')
    return false
  }

  if (!themeDesc) {
    ElMessage.warning('请输入主题说明')
    return false
  }

  if (!userRangePojoList.length) {
    ElMessage.warning('请选择本次任务派发给谁')
    return false
  }

  if (!dueTime) {
    ElMessage.warning('请选择任务截止时间')
    return false
  }

  return true
}

const validateRedispatchForm = () => {
  const dueTime = form.value.dueTime
  const userRangePojoList = normalizeUserRange(form.value.userRangePojoList)

  if (!userRangePojoList.length) {
    ElMessage.warning('请选择本次任务派发给谁')
    return false
  }

  if (!dueTime) {
    ElMessage.warning('请选择任务截止时间')
    return false
  }

  return true
}

const validateSubmitForm = () => {
  return dialogType.value === 'redispatch' ? validateRedispatchForm() : validateStepTwoForm()
}

const getProfilePayloadSnapshot = () => JSON.stringify(buildPreviewProfilesPayload())

const hasReusableProfiles = () => {
  return (
    profileStatus.value === 'done' &&
    form.value.customerProfileList?.length &&
    profilePayloadSnapshot.value === getProfilePayloadSnapshot()
  )
}

const handleGenerateProfiles = async () => {
  if (profileStatus.value === 'loading') {
    return
  }

  if (!validateStepTwoForm()) {
    return
  }

  if (hasReusableProfiles()) {
    activeStep.value = 'step-three'
    return
  }

  clearProfilePolling()
  const currentProfileToken = profilePollingToken
  activeStep.value = 'step-three'
  profileStatus.value = 'loading'
  clearProfiles()

  try {
    const profileResult = await generateCustomerProfileResult(currentProfileToken)
    ensureProfilePollingActive(currentProfileToken)
    const customerProfileList = normalizeCustomerProfileList(profileResult.customerProfileList)

    form.value = {
      ...form.value,
      customerProfileList,
      selectedProfileIds: customerProfileList.map((profile) => profile.id),
      profileNodeResult: profileResult.profileNodeResult || {}
    }
    profileStatus.value = 'done'
    activeStep.value = 'step-three'
  } catch (error) {
    // if (!error?.cancelled) {
    //   ElMessage.error(error?.message || '客户画像生成失败，请稍后重试')
    // }
    profileStatus.value = 'idle'
    activeStep.value = 'step-two'
  }
}

const handleSave = async () => {
  if (!validateSubmitForm()) {
    return
  }

  const selectedProfileList = getSelectedProfileList()

  if (!selectedProfileList.length) {
    ElMessage.warning('请至少选择 1 个客户画像')
    return
  }

  if (dialogType.value !== 'redispatch' && profileStatus.value !== 'done') {
    ElMessage.warning('请先生成客户画像')
    return
  }

  if (dialogType.value !== 'create' && !currentTaskId.value) {
    ElMessage.warning('未获取到任务信息，请稍后重试')
    return
  }

  if (saveLoading.value) {
    return
  }

  saveLoading.value = true

  try {
    await createThemePracticeTask(buildSubmitPayload())

    let successText = '保存成功'
    if (dialogType.value === 'create') {
      successText = '新建成功'
    } else if (dialogType.value === 'redispatch') {
      successText = '再次派发成功'
    }
    ElMessage.success(successText)

    if (dialogType.value === 'redispatch') {
      redispatchDialogVisible.value = false
    } else {
      taskDialogVisible.value = false
    }
    await refreshList()
  } finally {
    saveLoading.value = false
  }
}

const resetDialogState = () => {
  clearExtractPolling()
  clearProfilePolling()
  resetForm()
}

const handleDialogVisibleChange = (visible) => {
  if (!visible) {
    resetDialogState()
  }
}

watch(taskDialogVisible, handleDialogVisibleChange)
watch(redispatchDialogVisible, handleDialogVisibleChange)

onUnmounted(() => {
  clearExtractPolling()
  clearProfilePolling()
})
</script>

<style lang="scss" scoped>
.practice-assistant {
  width: 100%;
  height: 100%;

  :deep(.el-button) {
    border-radius: 8px;
  }
}
</style>

<style lang="scss">
.practice-assistant__close-confirm {
  width: 400px;
  padding: 0 !important;
  border: 0;
  border-radius: 24px !important;
  overflow: hidden;
  background: #fff; // 填充/纯白
  box-shadow: 0 2px 15px 2px rgba(0, 0, 0, 0.05); // 阴影

  .el-message-box__header {
    padding: 24px 56px 24px 24px;
  }

  .el-message-box__title {
    color: #323233; // 文字/Text-4 强调正文标题
    font-size: 18px; // 二级文字/加粗
    font-weight: 600;
    line-height: 26px;
  }

  .el-message-box__headerbtn {
    top: 25px;
    right: 24px;
    width: 24px;
    height: 24px;
  }

  .el-message-box__close {
    color: #646566; // 文字/Text-3 次强调正文标题
    font-size: 16px;
  }

  .el-message-box__content {
    padding: 0 24px;
  }

  .el-message-box__message {
    margin: 0;

    p {
      margin: 0;
      color: #323233; // 文字/Text-4 强调正文标题
      font-size: 14px; // 四级文字/常规
      font-weight: 400;
      line-height: 20px;
    }
  }

  .el-message-box__btns {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 20px 24px;
  }

  .el-button {
    height: 36px;
    padding: 8px 16px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;

    & + .el-button {
      margin-left: 12px;
    }
  }

  .el-button--default {
    border-color: #e5e6eb; // 线条/Line-2 一般线条
    background: #fff; // 填充/纯白
    color: #323233; // 文字/Text-4 强调正文标题
  }

  .el-button--primary {
    border-color: #7a52f4; // 拓展色/紫罗兰/Violets-6
    background: #7a52f4; // 拓展色/紫罗兰/Violets-6
    color: #fff; // 填充/纯白
  }

  .el-button--primary:hover {
    border-color: #7a52f4; // 拓展色/紫罗兰/Violets-6
    background: #7a52f4; // 拓展色/紫罗兰/Violets-6
  }
}
</style>
